import React, { useCallback, useEffect, useState } from "react";
import api, { brl, dateBR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, CheckCircle, Printer } from "lucide-react";
import { toast } from "sonner";
import { printQuote } from "@/components/QuotePrint";

const EMPTY = { customer: "", customer_id: "", items: [], discount: 0, validity_days: 7, notes: "" };

export default function Quotes() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [addProductId, setAddProductId] = useState("");

  const load = useCallback(() => {
    api.get("/quotes").then((r) => setItems(r.data));
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active !== false)));
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setAddProductId(""); setOpen(true); };

  const addItem = () => {
    const p = products.find((x) => x.id === addProductId);
    if (!p) return;
    if (form.items.find((i) => i.product_id === p.id)) return toast.error("Produto já adicionado");
    setForm((f) => ({ ...f, items: [...f.items, { product_id: p.id, name: p.name, quantity: 1, price: p.price }] }));
    setAddProductId("");
  };
  const updateItem = (idx, key, value) => setForm((f) => ({ ...f, items: f.items.map((i, j) => j === idx ? { ...i, [key]: value } : i) }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== idx) }));

  const subtotal = form.items.reduce((s, i) => s + Number(i.price) * Number(i.quantity), 0);
  const total = Math.max(0, subtotal - Number(form.discount || 0));

  const save = async () => {
    if (!form.items.length) return toast.error("Adicione pelo menos 1 item");
    try {
      const customerName = form.customer || (customers.find((c) => c.id === form.customer_id)?.name) || null;
      await api.post("/quotes", {
        customer: customerName, customer_id: form.customer_id || null,
        items: form.items.map((i) => ({ ...i, quantity: Number(i.quantity), price: Number(i.price) })),
        discount: Number(form.discount || 0),
        validity_days: Number(form.validity_days || 7),
        notes: form.notes || null,
      });
      setOpen(false);
      toast.success("Orçamento gerado");
      setTimeout(load, 350);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (id) => {
    if (!confirm("Excluir este orçamento?")) return;
    await api.delete(`/quotes/${id}`); toast.success("Excluído"); load();
  };

  const convert = async (id) => {
    if (!confirm("Converter em venda à vista?")) return;
    try {
      await api.post(`/quotes/${id}/convert`);
      toast.success("Convertido em venda!");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const printQuoteRow = async (q) => {
    let co = {};
    try { co = (await api.get("/company")).data || {}; } catch { co = {}; }
    printQuote(q, co);
  };

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Vendas</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Orçamentos</h1>
        </div>
        <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="new-quote-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Orçamento
        </Button>
      </div>

      <Card className="card-outline">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Data</TableHead><TableHead>Cliente</TableHead>
              <TableHead>Itens</TableHead><TableHead className="text-right">Total</TableHead>
              <TableHead>Validade</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((q) => (
                <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                  <TableCell className="text-sm">{dateBR(q.created_at)}</TableCell>
                  <TableCell>{q.customer || "—"}</TableCell>
                  <TableCell><Badge variant="outline">{q.items.length}</Badge></TableCell>
                  <TableCell className="text-right kpi-num text-blue-600">{brl(q.total)}</TableCell>
                  <TableCell className="text-sm">{q.valid_until}</TableCell>
                  <TableCell>
                    {q.status === "converted" ? <Badge className="bg-emerald-600">Convertido</Badge> : <Badge className="bg-amber-500">Aberto</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => printQuoteRow(q)} data-testid={`print-q-${q.id}`}><Printer className="w-4 h-4" /></Button>
                    {q.status !== "converted" && (
                      <Button size="icon" variant="ghost" className="text-emerald-600" onClick={() => convert(q.id)} data-testid={`convert-q-${q.id}`}><CheckCircle className="w-4 h-4" /></Button>
                    )}
                    <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => remove(q.id)} data-testid={`del-q-${q.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Nenhum orçamento.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="quote-dialog">
          <DialogHeader><DialogTitle className="font-display">Novo Orçamento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Cliente (opcional)</Label>
                <Select value={form.customer_id || "none"} onValueChange={(v) => setForm({ ...form, customer_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="q-customer"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Avulso</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Validade (dias)</Label>
                <Input type="number" value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: e.target.value })} data-testid="q-validity" /></div>
            </div>

            <div>
              <Label>Adicionar item</Label>
              <div className="flex gap-2">
                <Select value={addProductId} onValueChange={setAddProductId}>
                  <SelectTrigger className="flex-1" data-testid="q-add-product"><SelectValue placeholder="Selecione produto/serviço" /></SelectTrigger>
                  <SelectContent>
                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {brl(p.price)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" onClick={addItem} disabled={!addProductId} data-testid="q-add-btn"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>

            {form.items.length > 0 && (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="w-24 text-right">Qtd</TableHead>
                    <TableHead className="w-32 text-right">Preço R$</TableHead>
                    <TableHead className="w-24 text-right">Total</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {form.items.map((it, idx) => (
                      <TableRow key={it.product_id}>
                        <TableCell className="text-sm">{it.name}</TableCell>
                        <TableCell><Input type="number" step="1" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="h-8 text-right" /></TableCell>
                        <TableCell><Input type="number" step="0.01" value={it.price} onChange={(e) => updateItem(idx, "price", e.target.value)} className="h-8 text-right" /></TableCell>
                        <TableCell className="text-right kpi-num">{brl(Number(it.quantity) * Number(it.price))}</TableCell>
                        <TableCell><Button size="icon" variant="ghost" className="text-rose-600 h-7 w-7" onClick={() => removeItem(idx)}><Trash2 className="w-3 h-3" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Desconto</Label>
                <Input type="number" step="0.01" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} data-testid="q-discount" /></div>
              <div className="flex flex-col justify-end">
                <div className="text-xs text-slate-500">Total</div>
                <div className="kpi-num text-2xl text-blue-600">{brl(total)}</div>
              </div>
            </div>
            <Textarea placeholder="Observações" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="save-quote-btn">Gerar Orçamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
